import { APP_INITIALIZER, isDevMode, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { registerLocaleData } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CalendarCommonModule, CalendarWeekModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { OnlineStatusModule } from 'ngx-online-status';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { DayComponent } from './day/day.component';
import { SettingsComponent } from './settings/settings.component';
import { ManifestComponent } from './manifest/manifest.component';
import { DevComponent } from './dev/dev.component';
import { FileToImagePipe } from './components/file-to-image.pipe';
import { ImageViewerComponent } from './image-viewer/image-viewer.component';
import { LoginComponent } from './login/login.component';
import { AccountComponent } from './account/account.component';
import { ComponentsModule } from './components/components.module';
import { DayIndexBlockComponent } from './day/day-index-block/day-index-block.component';
import { IntroComponent } from './intro/intro.component';
import { RegisterComponent } from './register/register.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LandingComponent } from './landing/landing.component';
import { AuthService } from '../services/auth/auth.service';
import { catchError, distinctUntilChanged, filter, from, of, switchMap, take } from 'rxjs';
import { MinWriterComponent } from './min-writer/min-writer.component';
import { MinWriterMenuComponent } from './min-writer/min-writer-menu/min-writer-menu.component';
import { InitializeService } from '../services/initialize.service';
import { SupaStatisticsComponent } from './statistics/supa-statistics.component';
import { SupaStatisticsDayPopoverComponent } from './statistics/supa-statistics-day-popover.component';
import {
  JournalDayActivityCalendarComponent
} from './statistics/statistics-weekly-activity/journal-day-activity-calendar.component';

registerLocaleData(localeDe, localeDeExtra);

const initializeApp = (auth: AuthService, initializeService: InitializeService) => {
  return () => auth.user$.pipe(
    // Skip the initial `undefined` state — wait for Supabase to resolve the session
    filter(user => user !== undefined),
    // Only re-run when the login state itself flips (null↔User), not on token refreshes
    distinctUntilChanged((a, b) => !!a === !!b),
    switchMap(user => !!user
      ? from(initializeService.initialize()).pipe(
          catchError(err => {
            // Local Supabase not running, RLS error, etc. — log and continue
            console.error('[init] Failed to load initial data:', err);
            return of(null);
          })
        )
      : of(null)
    ),
    take(1)
  );
}

@NgModule({
  declarations: [
    LandingComponent,
    RegisterComponent,
    LoginComponent,
    AccountComponent,
    IntroComponent,

    AppComponent,

    DayComponent,
    DayIndexBlockComponent,

    SupaStatisticsComponent,
    JournalDayActivityCalendarComponent,
    SupaStatisticsDayPopoverComponent,

    MinWriterComponent,
    MinWriterMenuComponent,

    ManifestComponent,
    SettingsComponent,

    ImageViewerComponent,

    DevComponent,
    FileToImagePipe
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    IonicModule.forRoot({
      mode: 'md',
      toastDuration: 1500,
    }),
    FormsModule,
    ReactiveFormsModule,
    MarkdownModule.forRoot(),
    BrowserAnimationsModule,
    CalendarCommonModule.forRoot(
      {provide: DateAdapter, useFactory: adapterFactory}
    ),
    CalendarWeekModule,
    //NgOptimizedImage,
    OnlineStatusModule,

    ComponentsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [AuthService, InitializeService]
    },
    // ...(environment.production ? sentryProviders : []),
    {provide: LOCALE_ID, useValue: 'de'},
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
